import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Star, BookOpen, Download, ShoppingCart, Eye, Gift, Coins, X, BadgeCheck, ExternalLink, Plus, Check, Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PreviewDialog from "@/components/PreviewDialog";
import RecentlyPurchased from "@/components/RecentlyPurchased";
import FavoriteButton from "@/components/FavoriteButton";
import GiftDialog from "@/components/GiftDialog";
import WelcomeCoupon from "@/components/WelcomeCoupon";
import BookReviews from "@/components/BookReviews";
import SmartImage from "@/components/SmartImage";
import { captureRefFromUrl, clearRefCode } from "@/lib/referral";
import { slugifyName } from "@/lib/slug";

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add: addToCart, has: inCart } = useCart();
  const [book, setBook] = useState(null);
  const [owned, setOwned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [refCode, setRefCode] = useState(null);
  const [quote, setQuote] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [topReviews, setTopReviews] = useState([]);

  useEffect(() => {
    setRefCode(captureRefFromUrl());
  }, []);

  useEffect(() => {
    api.get(`/books/${id}`).then((r) => setBook(r.data)).catch(() => {});
    api.get(`/books/${id}/reviews`).then((r) => {
      const withComments = (r.data || [])
        .filter((rv) => rv.comment && rv.comment.trim().length > 0 && rv.rating >= 4)
        .sort((a, b) => (b.rating - a.rating) || (new Date(b.created_at) - new Date(a.created_at)))
        .slice(0, 2);
      setTopReviews(withComments);
    }).catch(() => setTopReviews([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!user || user === false) { setOwned(false); setQuote(null); return; }
    api.get("/library").then((r) => {
      setOwned(r.data.some((b) => b.id === id));
    }).catch(() => {});
    const params = {};
    if (refCode) params.referral_code = refCode;
    if (appliedCode) params.discount_code = appliedCode;
    api.get(`/referrals/quote/${id}`, { params })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, refCode, appliedCode]);

  const handleBuy = async () => {
    if (!user || user === false) {
      toast("Please sign in to purchase", { description: "Create a free account in a moment." });
      navigate("/login", { state: { from: `/book/${id}` } });
      return;
    }
    setLoading(true);
    try {
      const origin_url = window.location.origin;
      const payload = { book_id: id, origin_url, use_credits: true };
      if (refCode) payload.referral_code = refCode;
      if (appliedCode) payload.discount_code = appliedCode;
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  const dismissRef = () => {
    clearRefCode();
    setRefCode(null);
  };

  if (!book) return <div className="min-h-[60vh] flex items-center justify-center text-muted_ink">Loading…</div>;

  const cover = book.cover_url || (book.cover_path ? `${API}/files/${book.cover_path}` : null);

  return (
    <div data-testid="book-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
      <Link to="/browse" className="label-eyebrow text-muted_ink hover:text-terracotta">← Back to shelf</Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mt-8">
        {/* Sticky cover */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="aspect-[3/4] book-card-cover overflow-hidden bg-linen">
              {cover && <SmartImage src={cover} alt={book.title} className="w-full h-full object-cover" />}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-7">
          <div className="label-eyebrow text-sage">{book.category}</div>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-3 leading-[1]">{book.title}</h1>
          <div className="mt-4 text-xl text-muted_ink font-serif italic">
            by <Link
              to={`/author/${slugifyName(book.author)}`}
              data-testid="author-link"
              className="underline underline-offset-4 decoration-transparent hover:decoration-terracotta hover:text-terracotta transition-colors"
            >{book.author}</Link>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {book.shop_owned && (
              <span data-testid="badge-shop-owned" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-medium text-terracotta border border-terracotta/40 bg-terracotta/5 px-2.5 py-1">
                <BadgeCheck className="w-3 h-3" strokeWidth={2} />
                A Folio Edition
              </span>
            )}
            {book.external_url && (
              <span className="inline-block text-[10px] uppercase tracking-widest font-medium text-ink bg-linen border border-edge px-2.5 py-1">
                Public Domain Classic
              </span>
            )}
          </div>

          <div className="flex items-center gap-6 mt-6 text-sm">
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 fill-terracotta text-terracotta" />
              <span className="font-medium">{book.rating}</span>
              <span className="text-muted_ink">({book.reviews_count} reviews)</span>
            </div>
            <div className="text-muted_ink">{book.pages} pages</div>
          </div>

          <p className="mt-8 text-lg leading-relaxed text-ink">{book.description}</p>

          {/* Top verified reviews — above the fold social proof */}
          {topReviews.length > 0 && (
            <div data-testid="top-reviews-preview" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {topReviews.map((r) => (
                <a
                  key={r.id}
                  href="#reviews"
                  data-testid={`top-review-${r.id}`}
                  className="group relative border border-edge bg-paper/80 backdrop-blur-sm p-5 hover:border-terracotta/50 transition-colors"
                >
                  <Quote className="w-5 h-5 text-terracotta/70 mb-2" strokeWidth={1.5} />
                  <p className="font-serif italic text-ink leading-snug text-base line-clamp-4">
                    {r.comment}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-terracotta/10 text-terracotta font-serif text-sm flex items-center justify-center flex-shrink-0">
                        {(r.user_name || "R")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-ink truncate flex items-center gap-1">
                          {r.user_name}
                          {r.verified_purchase && (
                            <BadgeCheck className="w-3 h-3 text-sage flex-shrink-0" strokeWidth={2} />
                          )}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-sage">
                          Verified reader
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3 h-3 ${n <= r.rating ? "fill-terracotta text-terracotta" : "text-edge"}`}
                          strokeWidth={1.5}
                        />
                      ))}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {book.external_url && (
            <div data-testid="gutenberg-link" className="mt-6 border-l-2 border-sage/50 pl-4 text-sm text-muted_ink">
              <div className="label-eyebrow text-sage mb-1">Also freely available</div>
              This work is in the public domain. Read it at{" "}
              <a
                href={book.external_url}
                target="_blank"
                rel="noreferrer"
                className="text-ink underline underline-offset-4 decoration-terracotta inline-flex items-center gap-1 hover:text-terracotta"
              >
                Project Gutenberg <ExternalLink className="w-3 h-3" />
              </a>
              . Our Folio Edition adds a curated cover, notes, and a proper reading experience.
            </div>
          )}

          <div className="mt-10 flex items-baseline gap-4 border-t border-edge pt-8">
            <div className="font-serif text-5xl">${book.price?.toFixed(2)}</div>
            <div className="label-eyebrow text-muted_ink">Digital edition · DRM-free PDF</div>
          </div>

          {/* Referral / credits banner */}
          {!owned && quote && (quote.referral_valid || quote.code_valid || quote.credits_used > 0) && (
            <div data-testid="discount-banner" className="mt-4 border border-terracotta/40 bg-terracotta/5 p-4 flex items-start gap-3">
              <Gift className="w-5 h-5 text-terracotta mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 text-sm">
                {quote.referral_valid && (
                  <div data-testid="discount-row" className="text-ink">
                    Referral applied: <span className="font-medium text-terracotta">−${quote.referral_discount.toFixed(2)}</span>
                    <span className="text-muted_ink"> ({quote.discount_percent}% off)</span>
                  </div>
                )}
                {quote.code_valid && (
                  <div data-testid="code-row" className="text-ink">
                    Code <span className="font-mono">{appliedCode}</span> applied: <span className="font-medium text-terracotta">−${quote.code_discount.toFixed(2)}</span>
                    <span className="text-muted_ink"> ({quote.code_percent}% off)</span>
                  </div>
                )}
                {quote.credits_used > 0 && (
                  <div data-testid="credits-row" className="text-ink flex items-center gap-1">
                    <Coins className="w-3.5 h-3.5 text-terracotta" />
                    Credits applied: <span className="font-medium text-terracotta">−${quote.credits_used.toFixed(2)}</span>
                  </div>
                )}
                <div className="mt-2 font-serif text-2xl text-ink">
                  You pay <span className="text-terracotta">${quote.final.toFixed(2)}</span>
                </div>
              </div>
              {(quote.referral_valid || quote.code_valid) && (
                <button onClick={() => { dismissRef(); setAppliedCode(""); setCodeInput(""); }} className="text-muted_ink hover:text-ink" title="Clear discounts">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* Discount code input */}
          {!owned && (
            <div className="mt-4 flex gap-2 max-w-md">
              <input
                data-testid="discount-code-input"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="Have a code? Enter it here"
                className="flex-1 border border-edge bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-ink"
              />
              <button
                data-testid="apply-code-btn"
                type="button"
                onClick={async () => {
                  if (!codeInput) return;
                  try {
                    const { data } = await api.get(`/discount-codes/${codeInput}/validate`);
                    if (data.valid) {
                      setAppliedCode(codeInput);
                      toast.success(`${data.percent}% off applied`);
                    } else {
                      toast.error("Invalid or expired code");
                    }
                  } catch {
                    toast.error("Could not validate code");
                  }
                }}
                className="text-xs uppercase tracking-widest border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors"
              >
                Apply
              </button>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {owned ? (
              <>
                <Link
                  data-testid="read-btn"
                  to={`/read/${book.id}`}
                  className="btn-primary"
                >
                  <BookOpen className="w-4 h-4 mr-2" /> Read now
                </Link>
                <a
                  data-testid="download-btn"
                  href={`${API}/files/${book.pdf_path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </a>
              </>
            ) : (
              <>
                <button
                  data-testid="buy-btn"
                  onClick={handleBuy}
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> {loading ? "Starting checkout…" : "Buy this ebook"}
                </button>
                <button
                  data-testid="add-to-cart-btn"
                  onClick={() => addToCart(book)}
                  disabled={inCart(book.id)}
                  className="btn-secondary disabled:opacity-60"
                >
                  {inCart(book.id) ? (
                    <><Check className="w-4 h-4 mr-2" /> In cart</>
                  ) : (
                    <><Plus className="w-4 h-4 mr-2" /> Add to cart</>
                  )}
                </button>
                <button
                  data-testid="gift-btn"
                  onClick={() => setGiftOpen(true)}
                  className="btn-secondary"
                >
                  <Gift className="w-4 h-4 mr-2" /> Gift this book
                </button>
                {book.preview_path && (
                  <button
                    data-testid="preview-btn"
                    onClick={() => setPreviewOpen(true)}
                    className="btn-secondary"
                  >
                    <Eye className="w-4 h-4 mr-2" /> Read first 3 pages
                  </button>
                )}
                <FavoriteButton bookId={book.id} bookTitle={book.title} variant="button" />
              </>
            )}
          </div>

          {book.preview_path && (
            <PreviewDialog
              open={previewOpen}
              onOpenChange={setPreviewOpen}
              bookId={book.id}
              bookTitle={book.title}
            />
          )}

          <GiftDialog
            open={giftOpen}
            onOpenChange={setGiftOpen}
            bookId={book.id}
            bookTitle={book.title}
            bookAuthor={book.author}
            price={book.price}
          />

          {!owned && <WelcomeCoupon />}

          <BookReviews bookId={book.id} owned={owned} />

          <RecentlyPurchased bookId={book.id} bookTitle={book.title} />

          <div className="mt-12">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="about" className="border-b border-edge">
                <AccordionTrigger className="font-serif text-xl">About this book</AccordionTrigger>
                <AccordionContent className="text-muted_ink leading-relaxed">
                  {book.description}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="format" className="border-b border-edge">
                <AccordionTrigger className="font-serif text-xl">Format & delivery</AccordionTrigger>
                <AccordionContent className="text-muted_ink leading-relaxed">
                  Delivered as a DRM-free PDF, available instantly after purchase from your Library.
                  Readable on any device.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="returns" className="border-b border-edge">
                <AccordionTrigger className="font-serif text-xl">Returns</AccordionTrigger>
                <AccordionContent className="text-muted_ink leading-relaxed">
                  Not happy? Email us within 7 days for a full refund — no questions asked.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
}
